import i18n from '../i18n'
import { createBrowserHistory } from 'history'
import qs from 'query-string'

import { Analytics } from '../utils'
import { dutch, french } from '../i18n/languages'

const history = createBrowserHistory()

history.listen(location => {
    updateTitle()
})

const INTERNAL_TO_EXTERNAL = {
    nl: 'nld',
    fr: 'fra',
}
const EXTERNAL_TO_INTERNAL = {
    nld: dutch,
    fra: french,
}

const getRawParams = () => window.location.search

const querystringOptions = {
    parseBooleans: true,
    parseNumbers: true,
    arrayFormat: "separator",
    arrayFormatSeparator: "|",
}

const arrayParameters = [
    "value",
    "clause_lang",
    "year",
    "deed_type",
    "region",
    "deed_land_register_divisions_region",
]

const parseArrayParameters = params => {
    arrayParameters.forEach(querystringKey => {
        if (params[querystringKey] && !Array.isArray(params[querystringKey])) params[querystringKey] = [params[querystringKey]]
    })
    return params
}

const supportPreviousValueFormat = params => {
    const paramsCopy = { ...params }
    if (paramsCopy["value[]"] && !paramsCopy.value) {
        paramsCopy.value = paramsCopy["value[]"]
        delete paramsCopy["value[]"]
    }
    return paramsCopy
}

const getParams = () => {
    let params = qs.parse(getRawParams(), querystringOptions)
    params = supportPreviousValueFormat(params)
    params = parseArrayParameters(params)
    return params
}

const stringifyUrl = (path, params = null, bookmark = null) => {
    return qs.stringifyUrl({
        url: path,
        query: params,
        fragmentIdentifier: bookmark
    }, querystringOptions)
}

const getUrl = () => window.location.pathname

const getFullUrl = () => {
    const url = getUrl()
    const params = getParams()
    return stringifyUrl(url, params)
}

const updateQueryParams = newParams => {
    const url = getUrl()
    const params = getParams()
    goToUrl(url, {
        ...params,
        ...newParams,
    })
}

const pathRegexes = {
    search: {
        re: new RegExp("^/search$"),
        title: 'deedsearch',
    },
    home: {
        re: new RegExp("^/$"),
        title: 'home',
    },
    deed: {
        re: new RegExp("^/document/.*$"),
        title: 'deed',
    },
    similarClauses: {
        re: new RegExp("^/similar/.*$"),
        title: 'similar',
    },
    faq: {
        re: new RegExp("^/faq$"),
        title: 'faq',
    },
    error: {
        re: new RegExp("^/error$"),
        title: 'error',
    },
    profile: {
        re: new RegExp("^/profile$"),
        title: 'profile',
    }
}

const pathToTitle = (path) => {
    for (const reObject of Object.values(pathRegexes)) {
        if (reObject.re.test(path)) return i18n.t(`pages.${reObject.title}`)
    }
    return i18n.t('pages.deedsearch')
}

const isSearchPage = (urlToCheck = getUrl()) => {
    return pathRegexes.search.re.test(urlToCheck)
}

const isSimilarClausesPage = (urlToCheck = getUrl()) => {
    return pathRegexes.similarClauses.re.test(urlToCheck)
}

const isDeedPage = (urlToCheck = getUrl()) => {
    return pathRegexes.deed.re.test(urlToCheck)
}

const updateTitle = () => {
    setTimeout(() => {
        const path = getUrl()
        const title = pathToTitle(path)
        document.title = title
        Analytics.pageViewEvent(path, title)
    }, 500)
}

const goToUrl = (path, params = null, bookmark = null) => {
    if (params) delete params.lang
    const nextUrl = stringifyUrl(
        path,
        params,
        bookmark
    )
    history.push(nextUrl)
}

const updateLanguageFromUrl = () => {
    const { lang } = getParams()
    if (lang in EXTERNAL_TO_INTERNAL) {
        i18n.changeLanguage(EXTERNAL_TO_INTERNAL[lang])
    } else {
        i18n.changeLanguage(lang)
    }
}

const removeInvalidParams = (params, validParamNames) => {
    const validParams = {}
    Object.keys(params).forEach(key => {
        if (validParamNames.includes(key) && params[key] !== null) {
            validParams[key] = params[key]
        }
    })
    return validParams
}

const goToDocument = deedId => {
    const validParams = [
        "value",
        "lang",
    ]
    const currentParams = getParams()
    const newParams = removeInvalidParams(currentParams, validParams)
    goToUrl(`/document/${deedId}`, newParams)
}

const goToHome = () => {
    goToUrl('/')
}

const goToSearch = params => {
    const validParams = [
        "value",
        "lang",
        "page",
        "page_size",
        "deed_land_register_divisions_region",
        "region",
        "clause_lang",
        "year",
        "deed_type",
        "subsection",
    ]
    const defaultParams = {
        page: 1,
        page_size: 10,
    }
    const currentParams = getParams()
    let newParams = { ...defaultParams, ...currentParams, ...params }
    newParams = removeInvalidParams(newParams, validParams)

    goToUrl('/search', newParams)
}

const goToSimilarClauses = clauseId => {
    const validParams = [
        "value",
        "lang",
        "page",
        "page_size",
    ]
    const defaultParams = {
        page: 1,
        page_size: 10,
    }
    const currentParams = getParams()
    let newParams = { ...currentParams, ...defaultParams }
    newParams = removeInvalidParams(newParams, validParams)

    goToUrl(`/similar/${clauseId}`, newParams)
}

const goToCookiePolicy = () => {
    const validParams = [
        "value",
        "lang",
    ]
    const currentParams = getParams()
    const newParams = removeInvalidParams(currentParams, validParams)
    goToUrl('/cookies', newParams)
}

const goToFAQ = () => {
    goToUrl("/faq", getParams())
}

export {
    goToUrl, goToDocument, goToFAQ, goToHome, goToSearch, goToSimilarClauses, goToCookiePolicy,
    getParams, getFullUrl, getUrl, updateLanguageFromUrl, updateTitle, stringifyUrl, getRawParams,
    updateQueryParams, isSearchPage, isSimilarClausesPage, isDeedPage,
    history, EXTERNAL_TO_INTERNAL, INTERNAL_TO_EXTERNAL
}